import { cn } from '@cannect/lib/utils'
import { Link } from '@cannect/new-components/atoms'
import { LuArrowRightCircle } from 'react-icons/lu'

export type TJourneyCardProps = {
  title: string
  description: string
  ctaLabel: string
  path: string
  image: string
  className?: string
}

/**
 * Card de entrada de jornada do Novo Marketplace (Figma node 4431:471).
 *
 * O protótipo repete os mesmos três cards no hero e em "Qual é o próximo passo", substituindo as
 * pills de CTA que existiam nos dois lugares. Como o bloco é idêntico nas duas seções, ele vive
 * aqui em vez de ser duplicado.
 *
 * O card inteiro é o link — o rótulo com seta é afordância visual, não um segundo alvo. Por isso
 * a seta não é focável e o `aria-label` junta título e rótulo, para o leitor de tela anunciar o
 * destino sem depender do texto decorativo.
 *
 * Contraste: o arquivo usa `marketplace-600` (#839A50) no rótulo do CTA sobre a foto escura, o que
 * rende 3,57–4,36:1 nos três cards e reprova WCAG AA (texto de 12px não conta como grande). Aqui
 * usa `marketplace-500`, um passo acima na mesma rampa, que mede 5,57–6,81:1. O título em
 * `neutral-50` já passa com folga (10,7–13,1:1).
 *
 * As fotos foram recortadas para a janela visível de 376x101 declarada no arquivo, então
 * `object-cover` reproduz o enquadramento sem transformação.
 */
export const JourneyCard = ({ title, description, ctaLabel, path, image, className }: TJourneyCardProps) => (
  <Link
    to={path}
    aria-label={`${title} — ${ctaLabel}`}
    className={cn(
      'group relative flex min-w-0 flex-1 items-center overflow-hidden rounded-[16px] border border-solid border-marketplace-500 p-4 no-underline',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
      className
    )}>
    <img
      src={image}
      alt=""
      aria-hidden
      loading="lazy"
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover"
    />

    <span className="relative flex min-w-0 flex-1 flex-col gap-2">
      <span className="flex flex-col gap-[2px]">
        <span className="text-base font-semibold leading-normal text-neutral-50">{title}</span>
        <span className="text-xs font-light leading-[17.86px] text-neutral-50">{description}</span>
      </span>

      <span className="flex items-center justify-between">
        <span className="text-xs font-semibold leading-[1.4] text-marketplace-500 transition-colors group-hover:text-marketplace-300">
          {ctaLabel}
        </span>
        <LuArrowRightCircle
          aria-hidden
          className="h-4 w-4 shrink-0 text-marketplace-500 transition-colors group-hover:text-marketplace-300"
        />
      </span>
    </span>
  </Link>
)
